import {
  Activity,
  ArrowLeft,
  Book,
  MessageSquarePlus,
  Palette,
  Users,
} from "lucide-react";
import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import ThemedPage from "src/components/ThemedPage";
import SettingsFeedbackSection from "src/components/settings/SettingsFeedbackSection";
import SettingsMembersSection from "src/components/settings/SettingsMembersSection";
import SettingsThemesSection from "src/components/settings/SettingsThemesSection";
import SettingsActivitySection from "../components/settings/Activity";

import { UI_THEME_OPTIONS } from "src/lib/themeOptions";
import { selectTheme } from "src/store/themeSlice";
import SettingsChangelogSection from "../components/settings/SettingsChangelogSection";
import { hasPermission, Permission, UserRole } from "../lib/roles";
import { selectUserProfile } from "../store/authSlice";

const nunito = { fontFamily: "'Nunito', system-ui, sans-serif" } as const;

type SettingsSection =
  "themes" | "members" | "activity" | "feedback" | "changelog";

type SettingsNavItem = {
  id: SettingsSection;
  label: string;
  icon: typeof Palette;
  visible: boolean;
};

type SettingsPanelProps = {
  id: SettingsSection;
  activeSection: SettingsSection;
  tabListId: string;
  children: React.ReactNode;
};

function parseSection(value: string | null): SettingsSection {
  switch (value) {
    case "members":
      return "members";
    case "activity":
      return "activity";
    case "feedback":
      return "feedback";
    case "themes":
      return "themes";
    case "changelog":
      return "changelog";
    default:
      return "themes";
  }
}

function getNavButtonClassName(selected: boolean) {
  const baseClassName = [
    "group relative inline-flex shrink-0 items-center gap-3 overflow-hidden",
    "rounded-2xl border-2 px-4 py-3 text-sm font-extrabold",
    "transition-[transform,box-shadow,background-color,border-color,color] duration-150 ease-out",
    "focus:outline-none focus-visible:ring-4 focus-visible:ring-white/20",
    "active:translate-y-1 active:shadow-none",
    "md:w-full md:justify-start",
  ].join(" ");

  const selectedClassName = [
    "border-white/20 bg-white/10 text-white",
    "shadow-[0_5px_0_rgba(255,255,255,0.25)]",
  ].join(" ");

  const unselectedClassName = [
    "border-white/10 bg-gray-950/15 text-gray-300",
    "shadow-[0_5px_0_rgba(0,0,0,0.35)]",
    "hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/10 hover:text-white",
    "hover:shadow-[0_6px_0_rgba(0,0,0,0.35)]",
  ].join(" ");

  return `${baseClassName} ${selected ? selectedClassName : unselectedClassName}`;
}

function SettingsPanel({
  id,
  activeSection,
  tabListId,
  children,
}: SettingsPanelProps) {
  const selected = activeSection === id;

  return (
    <section
      role="tabpanel"
      id={`${tabListId}-${id}-panel`}
      aria-labelledby={`${tabListId}-${id}-tab`}
      hidden={!selected}
      className="min-h-0 flex-1 flex-col data-[active=true]:flex"
      data-active={selected}
    >
      {children}
    </section>
  );
}

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabListId = useId();

  const goBack = useCallback(() => {
    // React Router stamps an incrementing `idx` on history state; a value > 0
    // means there's an in-app entry to pop. Otherwise (fresh load / deep link)
    // fall back to the dashboard.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate("/");
  }, [navigate]);

  const activeSection = parseSection(searchParams.get("section"));
  const userProfile = useSelector(selectUserProfile);
  const currentThemeKey = useSelector(selectTheme);

  const role = userProfile?.role ?? UserRole.VIEWER;

  const canManageUsers = hasPermission(role, Permission.MANAGE_USERS);
  const canViewActivity = hasPermission(role, Permission.VIEW_ACTIVITY);

  const currentTheme =
    UI_THEME_OPTIONS.find((theme) => theme.key === currentThemeKey) ??
    UI_THEME_OPTIONS[0];

  const [refreshKeys, setRefreshKeys] = useState<
    Record<SettingsSection, number>
  >({
    themes: 0,
    members: 0,
    activity: 0,
    feedback: 0,
    changelog: 0,
  });

  useEffect(() => {
    setRefreshKeys((current) => ({
      ...current,
      [activeSection]: current[activeSection] + 1,
    }));
  }, [activeSection]);

  const navItems: SettingsNavItem[] = useMemo(
    () => [
      {
        id: "themes",
        label: "Themes",
        icon: Palette,
        visible: true,
      },
      {
        id: "members",
        label: "Members",
        icon: Users,
        visible: canManageUsers,
      },
      {
        id: "activity",
        label: "Activity",
        icon: Activity,
        visible: canViewActivity,
      },
      {
        id: "feedback",
        label: "Feedback",
        icon: MessageSquarePlus,
        visible: true,
      },
      {
        id: "changelog",
        label: "Release Notes",
        icon: Book,
        visible: true,
      },
    ],
    [canManageUsers, canViewActivity],
  );

  const visibleNavItems = useMemo(
    () => navItems.filter((item) => item.visible),
    [navItems],
  );

  const setSection = useCallback(
    (section: SettingsSection) => {
      setSearchParams(section === "themes" ? {} : { section }, {
        replace: true,
      });
    },
    [setSearchParams],
  );

  return (
    <ThemedPage className="flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden pb-4">
      <div
        className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-col px-4 py-6 sm:px-6 lg:px-8"
        style={nunito}
      >
        <header className="mb-4 shrink-0">
          <div className="flex items-center gap-6 sm:gap-10 lg:gap-24">
            <button
              type="button"
              onClick={goBack}
              className={[
                "mb-2 inline-flex items-center gap-2 rounded-xl px-1 py-1",
                "text-sm font-semibold text-gray-400 transition-colors",
                "hover:text-white",
                "focus:outline-none focus-visible:ring-4 focus-visible:ring-white/20",
              ].join(" ")}
            >
              <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
              Back
            </button>

            <div className="flex flex-col items-start gap-2">
              <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                Settings
              </h1>

              <p className="mt-0.5 hidden text-xs text-gray-400 sm:block sm:text-sm md:hidden">
                Personalize your workspace and manage your team.
              </p>
            </div>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row md:gap-6 lg:gap-8">
          <nav className="shrink-0 md:w-52" aria-label="Settings sections">
            <div
              id={tabListId}
              role="tablist"
              className={[
                "-mx-1 flex gap-2 overflow-x-auto overscroll-x-contain px-1 pb-1",
                "md:mx-0 md:flex-col md:overflow-visible md:px-0 md:pb-0",
                "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
              ].join(" ")}
            >
              {visibleNavItems.map(({ id, label, icon: Icon }) => {
                const selected = activeSection === id;

                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    id={`${tabListId}-${id}-tab`}
                    aria-selected={selected}
                    aria-controls={`${tabListId}-${id}-panel`}
                    onClick={() => setSection(id)}
                    className={getNavButtonClassName(selected)}
                  >
                    <span
                      className={[
                        "pointer-events-none absolute inset-0 bg-gradient-to-br transition-opacity duration-150",
                        currentTheme.colors,
                        selected ? "opacity-20" : "opacity-0",
                      ].join(" ")}
                      aria-hidden
                    />

                    <span
                      className={[
                        "relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-2",
                        "transition-[transform,background-color,border-color] duration-150",
                        selected
                          ? `border-transparent bg-gradient-to-br ${currentTheme.colors} shadow-[0_3px_0_rgba(0,0,0,0.22)]`
                          : "border-white/10 bg-white/5 group-hover:bg-white/10",
                      ].join(" ")}
                      aria-hidden
                    >
                      <Icon
                        className={[
                          "h-5 w-5 shrink-0 transition-colors",
                          selected
                            ? "text-white"
                            : "text-gray-400 group-hover:text-white",
                        ].join(" ")}
                      />
                    </span>

                    <span className="relative z-10 truncate">{label}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          <div
            className={[
              "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
              "rounded-[1.5rem] border border-white/10 bg-gray-950/35",
              "shadow-[0_10px_32px_rgba(0,0,0,0.35)] backdrop-blur-sm sm:p-5",
            ].join(" ")}
          >
            <SettingsPanel
              id="themes"
              activeSection={activeSection}
              tabListId={tabListId}
            >
              <SettingsThemesSection
                isActive={activeSection === "themes"}
                refreshKey={refreshKeys.themes}
              />
            </SettingsPanel>

            {canManageUsers && (
              <SettingsPanel
                id="members"
                activeSection={activeSection}
                tabListId={tabListId}
              >
                <SettingsMembersSection
                  isActive={activeSection === "members"}
                  refreshKey={refreshKeys.members}
                />
              </SettingsPanel>
            )}

            {canViewActivity && (
              <SettingsPanel
                id="activity"
                activeSection={activeSection}
                tabListId={tabListId}
              >
                <SettingsActivitySection
                  isActive={activeSection === "activity"}
                  refreshKey={refreshKeys.activity}
                />
              </SettingsPanel>
            )}

            <SettingsPanel
              id="feedback"
              activeSection={activeSection}
              tabListId={tabListId}
            >
              <SettingsFeedbackSection
                isActive={activeSection === "feedback"}
                refreshKey={refreshKeys.feedback}
              />
            </SettingsPanel>
            <SettingsPanel
              id="changelog"
              activeSection={activeSection}
              tabListId={tabListId}
            >
              <SettingsChangelogSection
                isActive={activeSection === "changelog"}
                refreshKey={refreshKeys.changelog}
              />
            </SettingsPanel>
          </div>
        </div>
      </div>
    </ThemedPage>
  );
};

export default Settings;
